"use client";

import { BlocksProductHeroCan } from "../../../../__generated__/contentful-types";
import { useImageLoading } from "../../../../helpers/ImageLoadingContext";
import { getContentfulImageUrl } from "../../../../helpers/contentful/images/getContentfulImageUrl";
import { slugifyTitle } from "../../../../helpers/contentful/slugifyTitle";
import { responsiveImageHelper } from "../../../../helpers/responsiveImageHelper";
import { useViewportDimensions } from "../../../../helpers/useViewportDimensions";
import { Kicker } from "../../../kicker/kicker";
import { CosmosButton, CosmosIconChevronRight } from "@cosmos/web-react";
import classNames from "classnames/bind";
import { Fragment } from "react";
import styles from "./product-hero-can.module.css";

const cx = classNames.bind(styles);

const BACKGROUND_WIDTH = 1440;
const TABLET_BREAKPOINT = 650;

export const ProductHeroCan = ({ block }: { block: BlocksProductHeroCan }) => {
  const { viewportWidth } = useViewportDimensions();

  const imageLoading = useImageLoading();

  return (
    <div className={cx("container")}>
      <div className={cx("inner")}>
        <div
          className={cx("content", {
            "content--with-background": !!block.heroBackground,
          })}
        >
          <div
            className={cx("text", {
              "text--centered": block.centerContent,
            })}
          >
            {block.kicker && (
              <h1 className={cx("kicker-wrapper")}>
                <Kicker
                  className={cx("kicker")}
                  kind="underlined"
                  text={block.kicker}
                />
              </h1>
            )}
            {block.kicker ? (
              <p
                className={cx("title", {
                  "title--centered": block.centerContent,
                })}
              >
                {block.title?.split(". ").map((line, index, lines) =>
                  index !== lines.length - 1 ? (
                    <Fragment key={index}>
                      {`${line}.`}
                      <br />
                    </Fragment>
                  ) : (
                    line
                  ),
                )}
              </p>
            ) : (
              <h1
                className={cx("title", {
                  "title--centered": block.centerContent,
                })}
              >
                {block.title?.split(". ").map((line, index, lines) =>
                  index !== lines.length - 1 ? (
                    <Fragment key={index}>
                      {`${line}.`}
                      <br />
                    </Fragment>
                  ) : (
                    line
                  ),
                )}
              </h1>
            )}
            {/*<h1
              className={cx("title", {
                "title--centered": block.centerContent,
              })}
            >
              {block.title?.split(". ").map((line, index, lines) =>
                index !== lines.length - 1 ? (
                  <Fragment key={index}>
                    {`${line}.`}
                    <br />
                  </Fragment>
                ) : (
                  line
                ),
              )}
            </h1>*/}
            <p
              className={cx("description", {
                "description--centered": block.centerContent,
                "description--has-disclaimer": block.disclaimer,
              })}
            >
              {block.description}
            </p>
            {block.disclaimer && (
              <span
                className={cx("disclaimer", {
                  "disclaimer--centered": block.centerDisclaimer,
                })}
              >
                {block.disclaimer}
              </span>
            )}
            {/* investigate: why can't we have buttons if a background exists? */}
            {!block.heroBackground && (
              <>
                {block.linkUrl && block.linkText && (
                  <div className={cx("button-wrapper")}>
                    {block.linkUrl && block.linkText && (
                      <CosmosButton
                        href={cleanLink(block.linkUrl)}
                        onClick={(element) => {
                          if (block.linkUrl?.trim().startsWith("#")) {
                            element.preventDefault();
                            if (history && history.pushState) {
                              history.pushState(
                                { info: "scrolled to ingredients" },
                                "",
                                cleanLink(block.linkUrl),
                              );
                            }
                            scrollBy(cleanLink(block.linkUrl).substring(1));
                          }
                        }}
                        kind="primary"
                        size={
                          viewportWidth < TABLET_BREAKPOINT ? "medium" : "large"
                        }
                        className={cx("button")}
                        iconPlacement="after"
                      >
                        <CosmosIconChevronRight
                          slot="icon"
                          className={cx("button-icon")}
                        />
                        {block.linkText}
                      </CosmosButton>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {!block.heroBackground && block.heroArtwork && (
            <div className={cx("can")}>
              {block.heroArtwork.url && (
                <div className={cx("can-image-wrapper")}>
                  <img
                    loading={imageLoading}
                    srcSet={responsiveImageHelper.createContentfulSrcSet(
                      block.heroArtwork.url,
                    )}
                    sizes={
                      block.artworkWithStamp
                        ? "(min-width: 1000px) 270px, (min-width: 650px) 235px, 230px"
                        : "(min-width: 1000px) 210px, (min-width: 650px) 182px, 175px"
                    }
                    className={cx("can-image", {
                      "can-image--stamp": block.artworkWithStamp,
                    })}
                    data-with-stamp={block.artworkWithStamp}
                    alt={block.heroArtwork.description ?? ""}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {block.heroBackground && (
          <>
            <div className={cx("background")}>
              <img
                className={cx("background-image")}
                loading={imageLoading}
                src={
                  block.heroBackground.url
                    ? getContentfulImageUrl(block.heroBackground.url, {
                        width: BACKGROUND_WIDTH,
                        height: undefined,
                      })
                    : undefined
                }
              />
              {block.heroArtwork?.url && (
                <div className={cx("background-can")}>
                  <img
                    loading={imageLoading}
                    srcSet={responsiveImageHelper.createContentfulSrcSet(
                      block.heroArtwork.url,
                    )}
                    sizes="(min-width: 1000px) 210px, 160px"
                    className={cx("background-can-image")}
                    alt={block.heroArtwork.description ?? ""}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function scrollBy(id: string): void {
  const MENU_HEIGHT_OFFSET = 80;
  const scrollElement = document.getElementById(id);

  if (scrollElement) {
    window.scrollBy({
      top: scrollElement.getBoundingClientRect().top - MENU_HEIGHT_OFFSET,
      behavior: "smooth",
    });
  }
}

function cleanLink(url: string): string {
  if (url.startsWith("#")) {
    return `#${slugifyTitle(url.substring(1))}`;
  }
  return url;
}
